<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>

		<section class="package-item-section">
			<div class="row">
				<div class="columns column5">
					<picture>
						<source :srcset="page.cardImageWebp" type="image/webp" />
						<source :srcset="page.cardImage" />
						<img class="card-image" :src="page.cardImage" :alt="page.cardImageAlt" loading="lazy" />
					</picture>
				</div>
				<div class="columns column2 divider-column align-center">
					<div class="vertical-divider" />
				</div>
				<div class="columns column5 align-left">
					<main-content
						:title="page.title || page.header"
						:content="page.content"
						:intro-image="{
							image: page.introductionImage,
							imageWebp: page.introductionImageWebp,
						}"
					>
						<template #extra-content>
							<div class="special-content">
								<h4>{{ $t('including') }}</h4>
								<div v-parse-links v-html="page.sectionIncludesContent" />
								<ul class="including">
									<li v-for="item in page.sectionIncludes" :key="item.ID">
										<div class="include-icon">
											<!-- <font-awesome-icon :icon="`fal fa-${item.icon}`" size="1x" /> -->
											<font-awesome-icon :icon="`fal fa-check`" size="1x" />
										</div>
										<span>{{ item.text }}</span>
									</li>
								</ul>
							</div>
						</template>
					</main-content>
				</div>
				<div class="columns column12 align-center button-row">
					<nuxt-link class="button no-fill" :to="localePath('packages')">
						{{ $t('buttonReturnToOverview') }}
					</nuxt-link>
					<nuxt-link v-if="page.bookingUrl" class="button button-cta" target="_blank" :to="page.bookingUrl">
						<span>{{ $t('bookThisPackage') }}</span>
					</nuxt-link>
					<book-button v-else :packageid="page.bookingID" class="button button-cta">
						<span>{{ $t('bookThisPackage') }}</span>
					</book-button>
				</div>
				<div class="columns column12 align-center bottom-content">
					<div v-parse-links v-html="page.bottomContent" />
				</div>
			</div>
		</section>

		<section v-if="page.footerSection" class="footer-images">
			<div v-for="item in page.footerSection" :key="item.ID">
				<div>
					<picture>
						<source :srcset="item.image1Webp" type="image/webp" />
						<source :srcset="item.image1" />
						<img :src="item.image1" :alt="item.image1Alt" loading="lazy" />
					</picture>
				</div>
				<div>
					<picture>
						<source :srcset="item.image2Webp" type="image/webp" />
						<source :srcset="item.image2" />
						<img :src="item.image2" :alt="item.image2Alt" loading="lazy" />
					</picture>
				</div>
				<div>
					<picture>
						<source :srcset="item.image3Webp" type="image/webp" />
						<source :srcset="item.image3" />
						<img :src="item.image3" :alt="item.image3Alt" loading="lazy" />
					</picture>
				</div>
			</div>
		</section>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const localePath = useLocalePath();

await fetchPage();
</script>

<style lang="scss" scoped>
.package-item-section {
	.row {
		max-width: 1440px;
		position: relative;
	}
}

.button-row .button {
	margin-right: 15px;
}

.divider-column {
	position: relative;
}

.vertical-divider {
	width: 1px;
	height: auto;
	background: #3c3834;
	position: absolute;
	inset: 0 0 60px;
	margin: 0 auto;
}

:deep(h1) {
	margin: 0 0 10px;
}

.card-image {
	max-width: 500px;
	position: sticky;
	left: 0;
	top: 100px;
	margin: 0 0 50px;
}

.special-content {
	padding: 40px 0;

	:deep(p) {
		margin: unset;
	}

	h3,
	h4 {
		color: var(--body-color);
		font-weight: 500;
		margin: 0 0 10px;
	}

	.available-at {
		display: grid;
		grid-template-columns: 1fr 1fr;
		align-content: center;

		div {
			margin: 5px 0;
			display: flex;
			flex-flow: row nowrap;
			justify-content: flex-start;
			align-items: center;

			span {
				font-size: var(--button-font-size);
				margin: 0;
				color: #000;
			}

			img {
				margin: 0 12px 0 0;
				width: 40px;
				height: 40px;
			}
		}
	}
}

.main-content {
	text-align: left;
	padding: 0;

	:deep(.columns) {
		text-align: left;
		padding: 0;
	}
}

ul.including {
	list-style-type: none;
	padding: 0;

	li {
		display: flex;
		flex-flow: row nowrap;
		justify-content: flex-start;
		align-items: center;
		margin: 4px 0;
	}
}

.include-icon {
	/*
	margin: 0 10px 0 0;
	min-width: 30px;
	*/
	margin: 1px 10px -1px 0;
	font-size: 20px;
	min-width: 23px;
}

.bottom-content {
	margin: 100px auto 0;
	font-size: 18px;

	&:last-child {
		margin: 50px auto;
	}
}

.footer-images {
	max-width: 1920px;

	div {
		display: flex;
		flex-flow: row nowrap;
		place-content: stretch space-between;

		img {
			width: 100%;
			height: 100%;
			max-width: none;
			object-fit: cover;
		}

		div {
			width: 28%;
			margin: 0 1%;

			&:first-child,
			&:last-child {
				width: 33%;
			}
		}
	}
}

@media (max-width: 1080px) {
	.divider-column {
		display: none;
	}

	.column5 {
		width: 100%;
	}

	.card-image {
		margin-left: auto;
		margin-right: auto;
		display: block;
	}
}
</style>
